import { citationScopeHref } from '@/lib/citations/navigation-scope';
import { pagesNavigationHref } from '@/lib/pages/navigation-scope';
import {
  IconAd,
  IconChartBar,
  IconChartLine,
  IconClick,
  IconCloudCog,
  IconCompass,
  IconCurrencyDollar,
  IconFileAnalytics,
  IconFileSearch,
  IconFileText,
  IconFileTime,
  IconFolders,
  IconGauge,
  IconGitFork,
  IconHome,
  IconLayoutDashboard,
  IconLayoutKanban,
  IconLink,
  IconEdit,
  IconListCheck,
  IconMessageHeart,
  IconMessages,
  IconNews,
  IconPuzzle,
  IconRadar,
  IconRobot,
  IconSearch,
  IconServer,
  IconSparkles,
  IconSettings,
  IconTable,
  IconTextScan2,
  IconTrophy,
  IconTopologyStar,
  IconBrandYoutube,
  type TablerIcon,
} from '@tabler/icons-react';
import { isAdsModuleEnabledClient } from '@/lib/ads/feature-flags';
import { copyBrandScopeSearchParams } from '@/utils/brandScope';
import { youtubeEnabled } from '@/lib/youtube/config';

export type AppNavigationItem = {
  label: string;
  href: string;
  icon: TablerIcon;
  description?: string;
};

export type AppNavigationSection = {
  title: string;
  items: AppNavigationItem[];
};

export type DataExplorerPreset = 'brand' | 'publisher';

export const WORKSPACE_HOME_HREF = '/app/workspace?new_chat=true';

const AI_SEARCH_PAGE_PREFIX = '/app/ai-search/';

export function getBrandSlugFromPathname(pathname: string) {
  const prefix = pathname.startsWith('/app/brands/') ? '/app/brands/' : AI_SEARCH_PAGE_PREFIX;
  if (!pathname.startsWith(prefix)) return null;

  const brandPath = pathname.slice(prefix.length);
  if (
    !brandPath ||
    brandPath === 'competitors' ||
    brandPath.startsWith('competitors/') ||
    brandPath === 'resolve-default'
  ) {
    return null;
  }

  const brandSlug = brandPath.split('/')[0];
  return brandSlug ? decodeURIComponent(brandSlug) : null;
}

function appendBrandScopeSearch(
  pathname: string,
  searchParams?: { get: (key: string) => string | null }
) {
  if (!searchParams) return pathname;
  const params = copyBrandScopeSearchParams(searchParams);
  const query = params.toString();
  return query ? `${pathname}?${query}` : pathname;
}

export function getResolvedNavHref({
  href,
  brandSlug,
  searchParams,
  pathname,
}: {
  href: string;
  pathname?: string;
  brandSlug?: string | null;
  searchParams?: { get: (key: string) => string | null };
}) {
  const citationRoutes = [
    '/app/topics',
    '/app/publishers',
    '/app/citation-report',
    '/app/citation-report/charts',
    '/app/channel-manager',
    '/app/citations',
    '/app/prompts',
    '/app/fan-outs',
    '/app/answers',
  ];
  const citationRoute = citationRoutes.some(
    route => href === route || href.startsWith(`${route}/`)
  );
  const preserveCitationScope = Boolean(
    pathname &&
      (citationRoutes.some(route => pathname === route || pathname.startsWith(`${route}/`)) ||
        /^\/app\/brands\/[^/]+\/fan-outs(?:\/|$)/.test(pathname))
  );
  const preserveChartScope =
    preserveCitationScope ||
    Boolean(pathname && /^\/app\/ai-search\/competitors\/charts(\/|$)/.test(pathname));
  const scopedHref =
    searchParams && preserveChartScope && href.startsWith('/app/ai-search/competitors/charts')
      ? appendBrandScopeSearch(href, searchParams)
      : searchParams &&
          citationRoute &&
          (preserveCitationScope ||
            (href.startsWith('/app/citation-report/charts') && preserveChartScope))
        ? citationScopeHref(href, searchParams)
        : pathname && searchParams
          ? pagesNavigationHref(href, pathname, searchParams)
          : href;
  if (!brandSlug) return scopedHref;

  const encodedBrandSlug = encodeURIComponent(brandSlug);
  const brandScopedDestinations: Record<string, string> = {
    '/app/ai-search': `/app/ai-search/${encodedBrandSlug}`,
    '/app/visibility': `/app/ai-search/${encodedBrandSlug}`,
    '/app/rank': `/app/ai-search/${encodedBrandSlug}/rank`,
    '/app/rank-tables': `/app/ai-search/${encodedBrandSlug}/rank-tables`,
    '/app/sentiment': `/app/ai-search/${encodedBrandSlug}/sentiment`,
    '/app/weekly-report': `/app/brands/${encodedBrandSlug}/weekly-report`,
    '/app/fan-outs': `/app/brands/${encodedBrandSlug}/fan-outs`,
  };
  const destination = brandScopedDestinations[href];
  if (!destination) return scopedHref;
  if (citationRoute)
    return preserveCitationScope ? citationScopeHref(destination, searchParams) : destination;
  return appendBrandScopeSearch(destination, searchParams);
}

export const WORKSPACE_NAVIGATION_ITEMS: AppNavigationItem[] = [
  { label: 'New chat', href: WORKSPACE_HOME_HREF, icon: IconEdit },
  { label: 'Data Explorer', href: '/app/data-explorer', icon: IconChartBar },
  { label: 'Objectives', href: '/app/objectives', icon: IconLayoutKanban },
  { label: 'Tasks', href: '/app/workspace?mode=tasks&tab=tasks', icon: IconListCheck },
  { label: 'Agents', href: '/app/agents', icon: IconTopologyStar },
  { label: 'North Star', href: '/app/north-star', icon: IconCompass },
  { label: 'Growth Plan', href: '/app/workspace?mode=growth-program', icon: IconGauge },
  { label: 'Docs & Assets', href: '/app/docs', icon: IconFileText },
  { label: 'Skills', href: '/app/skills', icon: IconSparkles },
  { label: 'Plugins', href: '/app/plugins', icon: IconPuzzle },
  { label: 'MCP', href: '/app/mcp', icon: IconServer },
  { label: 'Settings', href: '/app/settings', icon: IconSettings },
];

function getModuleNavigationSections(): AppNavigationSection[] {
  return [
    {
      title: 'Home',
      items: [
        {
          label: 'Home',
          href: WORKSPACE_HOME_HREF,
          icon: IconHome,
          description: 'Start a new chat or pick up where you left off.',
        },
      ],
    },
    {
      title: 'Market',
      items: [
        ...(youtubeEnabled()
          ? [
              {
                label: 'YouTube',
                href: '/app/youtube',
                icon: IconBrandYoutube,
                description: 'Follow videos, Shorts, competitors, and AI citations.',
              },
            ]
          : []),
        {
          label: 'Market Leaders',
          href: '/app/ai-search/competitors',
          icon: IconTrophy,
          description: 'See which brands lead your market in AI search.',
        },
        {
          label: 'Market Trends',
          href: '/app/ai-search/competitors/charts',
          icon: IconChartLine,
          description: 'Track how your market changes over time.',
        },
        {
          label: 'Visibility',
          href: '/app/visibility',
          icon: IconRadar,
          description: 'See how often your brand appears in AI answers.',
        },
        {
          label: 'Rankings',
          href: '/app/rank',
          icon: IconChartBar,
          description: 'Compare your brand with competitors across tracked questions.',
        },
        {
          label: 'Rank Details',
          href: '/app/rank-tables',
          icon: IconTable,
          description: 'Explore your ranking results question by question.',
        },
        {
          label: 'Sentiment',
          href: '/app/sentiment',
          icon: IconMessageHeart,
          description: 'Understand how AI talks about your brand.',
        },
        ...(isAdsModuleEnabledClient()
          ? [
              {
                label: 'Ads',
                href: '/app/ads',
                icon: IconAd,
                description: 'Review paid search performance and opportunities.',
              },
            ]
          : []),
        {
          label: 'Weekly Report',
          href: '/app/weekly-report',
          icon: IconFileTime,
          description: 'See what changed this week and what needs attention.',
        },
      ],
    },
    {
      title: 'Authority',
      items: [
        {
          label: 'Topics',
          href: '/app/topics',
          icon: IconFolders,
          description: 'Explore topic coverage and the publishers and pages earning citations.',
        },
        {
          label: 'Publisher Overview',
          href: '/app/publishers',
          icon: IconChartBar,
          description: 'See publisher performance, topic coverage, and most-cited content.',
        },
        {
          label: 'Page Tracker',
          href: '/app/publishers/tracker',
          icon: IconListCheck,
          description: 'Track custom page lists and their presence in AI answers.',
        },
        {
          label: 'Citation Sources',
          href: '/app/citation-report',
          icon: IconFileSearch,
          description: 'See which sources AI relies on when answering buyers.',
        },
        {
          label: 'Source Trends',
          href: '/app/citation-report/charts',
          icon: IconFileAnalytics,
          description: 'Track how the sources shaping AI answers change over time.',
        },
        {
          label: 'Publishers',
          href: '/app/channel-manager',
          icon: IconNews,
          description: 'Explore publishers, cited pages, brand mentions, and contact routes.',
        },
        {
          label: 'Cited Pages',
          href: '/app/citations',
          icon: IconLink,
          description: 'Inspect cited articles and the brands they mention.',
        },
      ],
    },
    {
      title: 'Web Analytics',
      items: [
        {
          label: 'Overview',
          href: '/app/pages/overview',
          icon: IconLayoutDashboard,
          description: 'Get a quick read on website traffic and performance.',
        },
        {
          label: 'Agent Check',
          href: '/app/pages/readiness',
          icon: IconRobot,
          description: 'Check whether AI agents can discover, understand, and act on your site.',
        },
        {
          label: 'Pages',
          href: '/app/pages',
          icon: IconFolders,
          description: 'See how each page on your website is performing.',
        },
        {
          label: 'Page Review',
          href: '/app/pages/review',
          icon: IconFileSearch,
          description: 'Review page messaging against buyer, search, AI, and behavior evidence.',
        },
        {
          label: 'GA4',
          href: '/app/pages/analytics/ga4',
          icon: IconGauge,
          description: 'Explore visits, engagement, and conversions from Google Analytics.',
        },
        {
          label: 'GSC',
          href: '/app/pages/analytics/gsc',
          icon: IconSearch,
          description: 'See the Google searches bringing people to your site.',
        },
        {
          label: 'Bing',
          href: '/app/pages/analytics/bing',
          icon: IconTextScan2,
          description: 'Review your visibility and clicks from Bing search.',
        },
        {
          label: 'Clarity',
          href: '/app/pages/analytics/clarity',
          icon: IconClick,
          description: 'Understand how people use and move through your site.',
        },
        {
          label: 'Cloudflare',
          href: '/app/pages/analytics/cloudflare',
          icon: IconCloudCog,
          description: 'Track traffic, site performance, and AI crawler activity.',
        },
      ],
    },
    {
      title: 'Commerce',
      items: [
        {
          label: 'Revenue Analytics',
          href: '/app/revenue',
          icon: IconCurrencyDollar,
          description: 'Connect marketing activity to Shopify revenue.',
        },
      ],
    },
    {
      title: 'Search Paths',
      items: [
        {
          label: 'Prompts',
          href: '/app/prompts',
          icon: IconMessages,
          description: 'See the questions buyers ask AI about your market.',
        },
        {
          label: 'Fan-outs',
          href: '/app/fan-outs',
          icon: IconGitFork,
          description: 'Explore the related questions AI uses to build an answer.',
        },
        {
          label: 'AI Answers',
          href: '/app/answers',
          icon: IconRobot,
          description: 'Read the AI answers your buyers are seeing.',
        },
      ],
    },
  ];
}

/** Group destinations by what the user wants to open, rather than by data source. */
export function getAppNavigationSections(
  preset: DataExplorerPreset = 'brand'
): AppNavigationSection[] {
  const modules = new Map(
    getModuleNavigationSections()
      .flatMap(section => section.items)
      .map(item => [item.href, item])
  );
  const pageLabels: Record<string, string> = {
    '/app/ai-search/competitors': 'Top Recommended Brands',
    '/app/visibility': 'Brand Visibility',
    '/app/sentiment': 'Brand Sentiment',
    '/app/weekly-report': 'Weekly Brand Report',
    '/app/pages/overview': 'Website Overview',
    '/app/rank-tables': 'Brand Ranks by Prompt',
    '/app/prompts': 'Tracked Prompts',
    '/app/fan-outs': 'Search Fan-outs',
    '/app/channel-manager': 'Publisher Directory',
    '/app/pages': 'Website Pages',
    '/app/pages/analytics/ga4': 'Google Analytics (GA4)',
    '/app/pages/analytics/gsc': 'Google Search Console (GSC)',
    '/app/pages/analytics/bing': 'Bing Webmaster Tools',
    '/app/pages/analytics/clarity': 'Microsoft Clarity',
    '/app/pages/analytics/cloudflare': 'Cloudflare Analytics',
  };
  const pick = (...hrefs: string[]) =>
    hrefs.flatMap(href => {
      const item = modules.get(href);
      return item ? [{ ...item, label: pageLabels[href] || item.label }] : [];
    });
  const sections: AppNavigationSection[] = [
    { title: 'Home', items: pick(WORKSPACE_HOME_HREF) },
    {
      title: 'Brand Charts',
      items: [
        {
          label: 'Brand Share of Voice',
          href: '/app/ai-search/competitors/charts',
          icon: IconChartBar,
          description: 'Share of top-three placements across competing brands.',
        },
        {
          label: 'Brand Visibility',
          href: '/app/ai-search/competitors/charts/visibility',
          icon: IconRadar,
          description: 'Percentage of answers mentioning each brand over time.',
        },
        {
          label: 'Brand Top-3 Rate',
          href: '/app/ai-search/competitors/charts/top-3',
          icon: IconChartLine,
          description: 'Percentage of answers placing each brand in the top three.',
        },
        {
          label: 'Brand First-Place Rate',
          href: '/app/ai-search/competitors/charts/first-place',
          icon: IconTrophy,
          description: 'Percentage of answers ranking each brand first.',
        },
      ],
    },
    {
      title: 'Publisher Charts',
      items: [
        {
          label: 'Publisher Citation Share',
          href: '/app/citation-report/charts',
          icon: IconChartBar,
          description: 'Share of citations earned by each source domain over time.',
        },
        {
          label: 'Publisher Answer Coverage',
          href: '/app/citation-report/charts/answer-coverage',
          icon: IconChartLine,
          description: 'Percentage of answers citing each domain over time.',
        },
        {
          label: 'Publisher Topic Coverage',
          href: '/app/citation-report/charts/topic-coverage',
          icon: IconTable,
          description: 'Compare domain answer coverage and changes topic by topic.',
        },
      ],
    },
    {
      title: 'Topics & Answers',
      items: pick('/app/topics', '/app/prompts', '/app/answers', '/app/fan-outs'),
    },
    {
      title: 'Brand Reports',
      items: pick(
        '/app/ai-search/competitors',
        '/app/visibility',
        '/app/rank-tables',
        '/app/sentiment',
        '/app/weekly-report'
      ),
    },
    {
      title: 'Publisher Reports',
      items: pick(
        '/app/publishers',
        '/app/publishers/tracker',
        '/app/citation-report',
        '/app/channel-manager',
        '/app/citations'
      ),
    },
    {
      title: 'Website Reports',
      items: pick('/app/pages/overview', '/app/pages/readiness', '/app/pages/review'),
    },
    {
      title: 'Website Data',
      items: pick(
        '/app/pages',
        '/app/pages/analytics/ga4',
        '/app/pages/analytics/gsc',
        '/app/pages/analytics/bing',
        '/app/pages/analytics/clarity',
        '/app/pages/analytics/cloudflare'
      ),
    },
    {
      title: 'Commerce',
      items: pick('/app/revenue', '/app/ads'),
    },
    ...(modules.has('/app/youtube') ? [{ title: 'Video', items: pick('/app/youtube') }] : []),
  ];
  const first =
    preset === 'publisher'
      ? [
          'Home',
          'Publisher Charts',
          'Topics & Answers',
          'Publisher Reports',
          'Brand Charts',
          'Brand Reports',
        ]
      : [
          'Home',
          'Brand Charts',
          'Brand Reports',
          'Publisher Charts',
          'Topics & Answers',
          'Publisher Reports',
        ];
  const rank = new Map(first.map((title, index) => [title, index]));
  return sections.sort(
    (a, b) => (rank.get(a.title) ?? first.length) - (rank.get(b.title) ?? first.length)
  );
}
